import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { Button, Divider, Form, Input, Modal, notification, Typography } from 'custom-test-antd';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';

import './InviteCollaboratorsModal.pcss';

import { InputWrapper } from 'utils/form-helpers/antd-formik';

import { useOnboardingState } from '../context/onboardingContext';
import { sharedFlowSelector } from '../context/onboardingSelectors';

interface InviteCollaboratorsModalProps {
  open: boolean;
  onClose: () => void;
  onShareFlow: (collaboratorEmail?: string) => Promise<void>;
}

const emailValidationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
});

export const InviteCollaboratorsModal: React.FC<InviteCollaboratorsModalProps> = ({ open, onClose, onShareFlow }) => {
  const state = useOnboardingState();
  const sharedFlow = sharedFlowSelector(state);
  const flowLinkText = `${window.location.origin}/onboarding?flowId=${sharedFlow.id}`;
  const [countOfAwaitedShareFlowCalls, setCountOfAwaitedShareFlowCalls] = useState(0);
  useEffect(() => {
    if (open) {
      setCountOfAwaitedShareFlowCalls((v) => v + 1);
      onShareFlow().finally(() => {
        setCountOfAwaitedShareFlowCalls((v) => v - 1);
      });
    }
  }, [open, onShareFlow]);
  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(flowLinkText)
      .then(() => {
        notification.success({
          message: 'Link Copied',
          description: 'The invite link has been copied to your clipboard.',
          icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
          placement: 'topRight',
          duration: 3,
        });
      })
      .catch((err) => {
        notification.error({
          message: 'Copy Failed',
          description: 'Failed to copy the link. Please try again.',
          placement: 'topRight',
          duration: 3,
        });
        console.error('Failed to copy text: ', err);
      });
  };
  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onClose}
      closeIcon={<CloseOutlined />}
      width={600}
      className="invite-collaborators-modal"
      maskClosable
    >
      <div className="modal-content">
        <div className="modal-header">
          <Typography.Title level={3} className="modal-title">
            <span className="icon">👥</span>
            Invite Collaborators
          </Typography.Title>
          <Typography.Paragraph className="modal-subtitle">
            Share this link with team members to help add facility credentials. The link expires in 28 hours.
          </Typography.Paragraph>
        </div>
        <div className="link-section">
          <div className="section-title">
            <LinkOutlined />
            Sharable Link
          </div>
          <div className="link-container">
            <Input value={flowLinkText} readOnly className="link-input" />
            <Button
              icon={<CopyOutlined />}
              className="copy-button"
              disabled={countOfAwaitedShareFlowCalls > 0}
              onClick={handleCopyLink}
              loading={countOfAwaitedShareFlowCalls > 0}
            />
          </div>
        </div>
        <Divider className="divider">
          <span className="divider-text">OR SEND INVITE VIA EMAIL</span>
        </Divider>
        <div className="email-section">
          <div className="section-title">
            <MailOutlined />
            Invite by Email
          </div>
          <Formik
            initialValues={{ email: '' }}
            validationSchema={emailValidationSchema}
            onSubmit={async ({ email }, { resetForm }) => {
              setCountOfAwaitedShareFlowCalls((v) => v + 1);
              await onShareFlow(email);
              setCountOfAwaitedShareFlowCalls((v) => v - 1);
              resetForm();
            }}
          >
            {({ handleSubmit, isSubmitting, isValid }) => (
              <Form onFinish={handleSubmit} layout="inline">
                <InputWrapper
                  name="email"
                  placeholder="colleague@example.com"
                  className="email-input"
                  antFieldProps={{
                    style: {
                      flex: 1,
                    },
                  }}
                />
                <Button
                  type="primary"
                  icon={<MailOutlined />}
                  htmlType="submit"
                  disabled={!isValid || isSubmitting}
                  className="send-button"
                  loading={isSubmitting}
                >
                  Send Invite
                </Button>
              </Form>
            )}
          </Formik>
        </div>
        <div className="permissions-section">
          <div className="info-box">
            <div className="info-header">
              <InfoCircleOutlined />
              Collaborator Permissions
            </div>
            <ul className="permissions-list">
              <li>View list of credentials already added (passwords hidden).</li>
              <li>Add new facility credentials.</li>
              <li>Cannot edit or delete previously added credentials.</li>
            </ul>
            <div className="expiry-info">
              <ClockCircleOutlined />
              Link expires in 28 hours.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <Button onClick={onClose} size="large" className="close-button">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};
